import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';

import { type IMultiselectOption } from '@/components/Shared/ui/multiselect/multiselect';
import { type DictTypes } from '@/constants';
import { useAppSelector } from '@/store';
import { type GetDictContentWithExtrasType } from '@/types/dicts';

import { statusOptions } from '../data/statusOptions.data';

function isDate(date: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(date);
}

const useFilterParamValues = () => {
  const { dicts } = useAppSelector((state) => state.dict);
  const { dealer: userDealer } = useAppSelector((state) => state.userInfo);

  const [params] = useSearchParams();

  const activeParamDealers: (IMultiselectOption & {
    uuid: string;
    brand_uuid: string;
  })[] = useMemo(() => {
    const convert = (
      item: GetDictContentWithExtrasType<DictTypes.Dealers>
    ): IMultiselectOption & { uuid: string; brand_uuid: string } => {
      return {
        uuid: item.uuid,
        value: item.code.toString(),
        label: item.name.toString(),
        brand_uuid: item.sold_brands || ''
      };
    };

    const paramValue = params.get('dealers');
    if (!paramValue) {
      const userDealerItem = dicts?.Dealers?.content.find(
        (item) => item.code === userDealer?.code
      );
      if (userDealerItem) return [convert(userDealerItem)];
      return [];
    }
    const selectedDealers = paramValue.split(',');
    return (
      dicts?.Dealers?.content.filter((item) =>
        selectedDealers.includes(item.code.toString())
      ) || []
    ).map(convert);
  }, [params, dicts?.Dealers?.content, userDealer?.code]);

  const activeParamBanks: IMultiselectOption[] = useMemo(() => {
    const convert = (
      item: GetDictContentWithExtrasType<DictTypes.Banks>
    ): IMultiselectOption => {
      return {
        value: item.uuid,
        label: item.fullname
      };
    };

    const paramValue = params.get('banks');
    if (!paramValue) return [];

    const selectedBanks = paramValue.split(',');
    return (
      dicts?.Banks?.content.filter((item) =>
        selectedBanks.includes(item.uuid.toString())
      ) || []
    ).map(convert);
  }, [params, dicts?.Banks?.content]);

  const activeParamPeriod: [string, string] = useMemo(() => {
    const paramValue = params.get('period');
    if (!paramValue) return ['', ''];
    const selectedPeriod = paramValue.split(',');
    if (
      selectedPeriod.length !== 2 ||
      selectedPeriod.some((item) => !isDate(item))
    )
      return ['', ''];
    return selectedPeriod as [string, string];
  }, [params]);

  const activeOnlineRequest: string = useMemo(() => {
    const paramValue = params.get('online-request');
    if (!paramValue) return 'all';
    if (paramValue !== 'online' && paramValue !== 'offline') return 'all';
    return paramValue;
  }, [params]);

  const activeParamModels: IMultiselectOption[] = useMemo(() => {
    const paramValue = params.get('models');
    if (!paramValue || activeParamDealers.length === 0) return [];
    const selectedModelUuids = paramValue.split(',');

    const models = dicts?.Models?.content || [];
    const brands = dicts?.Brands?.content || [];
    const brandMap = new Map(brands.map((b) => [b.uuid, b.name]));

    return (
      models
        .filter((item) => selectedModelUuids.includes(item.uuid))
        .map((item) => ({
          label: `${item.brand_uuid ? `${brandMap.get(item.brand_uuid)} ` : ''}${item.name.toString()}`,
          value: item.uuid
        })) || []
    );
  }, [
    params,
    activeParamDealers,
    dicts?.Models?.content,
    dicts?.Brands?.content
  ]);

  const activeParamStatuses: typeof statusOptions = useMemo(() => {
    const paramValue = params.get('statuses');
    if (!paramValue) return [];
    const selectedStatuses = paramValue.split(',');
    return statusOptions.filter((item) =>
      selectedStatuses.includes(item.value)
    );
  }, [params]);

  const getModelOptionsBySelectedDealers = useCallback(
    (targetDealerCodes: string[]): IMultiselectOption[] => {
      if (targetDealerCodes.length === 0) return [];

      const dealers = dicts?.Dealers?.content || [];
      const dealersBrands = dicts?.Dealers_Brands?.content || [];
      const brands = dicts?.Brands?.content || [];
      const models = dicts?.Models?.content || [];

      const targetDealers = new Set(targetDealerCodes);
      const targetDealerUuids = new Set(
        dealers
          .filter((d) => targetDealers.has(d.code.toString()))
          .map((d) => d.uuid)
      );

      const uniqueBrandUuids = new Set(
        dealersBrands
          .filter((db) => targetDealerUuids.has(db.dealer_uuid))
          .map((db) => db.brand_uuid)
      );

      const brandMap = new Map(brands.map((b) => [b.uuid, b.name]));

      dealers.forEach((d) => {
        if (d.sold_brands && targetDealerUuids.has(d.uuid)) {
          uniqueBrandUuids.add(d.sold_brands);
        }
      });

      return models
        .filter((m) => m.brand_uuid && uniqueBrandUuids.has(m.brand_uuid))
        .map((m) => ({
          label: `${m.brand_uuid ? `${brandMap.get(m.brand_uuid)} ` : ''}${m.name}`,
          value: m.uuid
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    },
    [dicts]
  );

  return {
    activeOnlineRequest,
    activeParamBanks,
    activeParamDealers,
    activeParamModels,
    activeParamPeriod,
    activeParamStatuses,
    getModelOptionsBySelectedDealers
  };
};

export default useFilterParamValues;
